import React, { useEffect, useState } from 'react'

import { useParams, useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'
import { Box, Button } from '@material-ui/core'
import { HelpCircle as HelpCircleIcon } from 'react-feather'
import { isEmpty } from 'lodash'

import { ContentHeader, Page } from 'components'
import {
  EvidencesSkeleton,
  QuestionSkeleton,
  QuizQuestionTemplateForm,
  QuestionTutorialDialog,
} from '../components'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'

import { routes } from 'SupplierRoutes'
import * as service from 'service'
import helpers from 'helpers'

const QuizQuestionTemplateEdit = () => {
  const [evidences, setEvidences] = useState([])
  const [isLoadingEvidences, setIsLoadingEvidences] = useState(false)
  const [openTutorialDialog, setOpenTutorialDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { companySupplierQuestionnaireId, quizQuestionTemplateId } = useParams()
  const snackbar = useSnackbar()
  const history = useHistory()

  const { response, isLoading } = useFetch(
    service.supplierPortal.quizQuestionTemplates.get,
    { companySupplierQuestionnaireId, id: quizQuestionTemplateId },
    [quizQuestionTemplateId],
  )

  const {
    response: questionnairesResponse,
    isLoading: isLoadingQuestionnaires,
  } = useFetch(service.supplierPortal.companySupplierQuestionnaires.get, {})

  const quizQuestionTemplate = response?.data?.quizQuestionTemplate
  const answer = quizQuestionTemplate?.answer

  const companySupplierQuestionnaires =
    questionnairesResponse?.data?.companySupplierQuestionnaires

  const { previousQuestion, nextQuestion } =
    helpers.suppliers.companySuppliers.getSiblingQuestions(
      companySupplierQuestionnaires,
      companySupplierQuestionnaireId,
      quizQuestionTemplateId,
    )

  const handleOpenTutorialDialog = () => setOpenTutorialDialog(true)
  const handleCloseTutorialDialog = () => setOpenTutorialDialog(false)

  const redirectToHome = () => history.push(routes.home)

  const navigateToQuestion = (targetQuestion) => {
    const routeKey = targetQuestion.hasAnswer ? 'edit' : 'new'
    history.push(
      reverse(routes.quizQuestionTemplate[routeKey], {
        companySupplierQuestionnaireId,
        quizQuestionTemplateId: targetQuestion.id,
      }),
    )
  }

  const onSubmit = async (data, targetQuestion) => {
    setIsSubmitting(true)
    try {
      await service.supplierPortal.questionnaireAlternativeAnswers.update({
        companySupplierQuestionnaireId,
        quizQuestionTemplateId,
        id: answer?.id,
        params: data,
      })

      snackbar.open({
        message: 'Resposta atualizada com sucesso',
        variant: 'success',
      })

      if (targetQuestion) {
        navigateToQuestion(targetQuestion)
      } else {
        redirectToHome()
      }
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao atualizar a resposta da questão.',
        variant: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (!answer) return

    setIsLoadingEvidences(true)

    const loadEvidences = async () => {
      const apiFileToFile = async (apiFile) => {
        try {
          const response = await fetch(apiFile.url)
          const blob = await response.blob()
          return new File([blob], apiFile.name, { type: blob.type })
        } catch {
          return null
        }
      }

      let entries = {}

      const evidenceList = helpers.questionnaires.resolveEvidences(
        answer,
        quizQuestionTemplate,
      )

      const loadEvidenceFiles = async () => {
        for await (let evidence of evidenceList) {
          if (isEmpty(evidence.evidences)) {
            entries[evidence.id] = { files: [] }
            continue
          }

          let files = []

          for await (let apiFile of evidence.evidences) {
            const file = await apiFileToFile(apiFile)
            files.push(file)
          }

          entries[evidence.id] = { files: files.filter(Boolean) }
        }
      }

      await loadEvidenceFiles()
      setEvidences(entries)
      setIsLoadingEvidences(false)
    }

    loadEvidences()
  }, [answer, quizQuestionTemplate])

  const isPageLoading =
    isLoading || isLoadingQuestionnaires || !answer || isLoadingEvidences

  return (
    <Page title="Editar Questão de Formulário de Fornecedor">
      <Box px={2}>
        {isPageLoading ? (
          <Box display="flex" flexDirection="column" gridGap={8}>
            <QuestionSkeleton />
            <EvidencesSkeleton />
          </Box>
        ) : (
          <>
            <ContentHeader
              title={quizQuestionTemplate?.questionnaire?.kind}
              historyBreadcumbs={false}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<HelpCircleIcon size={20} />}
                onClick={handleOpenTutorialDialog}
                fullWidth
              >
                Tutorial
              </Button>
            </ContentHeader>
            <QuizQuestionTemplateForm
              key={quizQuestionTemplateId}
              quizQuestionTemplate={quizQuestionTemplate}
              onSubmit={onSubmit}
              onNavigate={navigateToQuestion}
              redirectToHome={redirectToHome}
              answer={answer}
              evidences={evidences}
              previousQuestion={previousQuestion}
              nextQuestion={nextQuestion}
              isSubmitting={isSubmitting}
            />
          </>
        )}
      </Box>

      {openTutorialDialog && (
        <QuestionTutorialDialog
          open={openTutorialDialog}
          setOpen={setOpenTutorialDialog}
          onClose={handleCloseTutorialDialog}
        />
      )}
    </Page>
  )
}

export default QuizQuestionTemplateEdit
